import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import { formatEUR, ttc } from "@/config/pricing";

/**
 * /services — Operational landing page describing what HorsIndex
 * concretely takes in charge on behalf of its clients.
 *
 * The page answers a single visitor question:
 *   "What do you take in charge for me?"
 *
 * It is intentionally shorter than /methode (which explains how
 * HorsIndex proceeds methodologically).
 *
 * Structure: hero (PageLayout) + 5 sections + footer (CTA built into
 * section 05, default PageLayout footer CTA disabled).
 *   01. Les situations prises en charge  (7 cards — situations + traitement)
 *   02. Comment se déroule une intervention  (4 short steps)
 *   03. Formules d'intervention  (4 cards — Finalité + Pris en charge)
 *   04. Ce qui reste hors périmètre  (3 short cards)
 *   05. Pourquoi un audit initial ?  (explainer + CTA button)
 */

const EXPOSITIONS = [
  {
    title: "Recherche et cartographie de l'exposition publique",
    traitement:
      "Recenser ce qui est librement accessible : pages, résultats indexés, documents, cartes et fiches rattachées à la personne concernée.",
  },
  {
    title: "Pages accessibles sans authentification",
    traitement:
      "Identifier les pages consultables sans compte ni justification particulière et qualifier les données qu'elles exposent.",
  },
  {
    title: "Retrait, masquage ou limitation de visibilité",
    traitement:
      "Préparer des demandes ciblées de retrait de la page publique, de masquage des données personnelles ou de restriction d'accès, lorsque le cadre le permet.",
  },
  {
    title: "Désindexation nominative",
    traitement:
      "Construire des demandes de désindexation auprès des moteurs de recherche et des moteurs internes des plateformes, lorsque la situation le justifie.",
    link: {
      to: "/desindexation",
      label: "Service dédié : déréférencement Google",
    },
  },
  {
    title: "Documents publiquement téléchargeables",
    traitement:
      "Demander le retrait ou la limitation de fichiers contenant des données personnelles, facilement copiés et remis en circulation.",
  },
  {
    title: "Cartes, adresses et liens capitalistiques",
    traitement:
      "Traiter les représentations géographiques et les mises en relation entre personnes, entreprises, adresses et participations.",
  },
  {
    title: "Suivi des réponses et dossier de preuve",
    traitement:
      "Suivre les échanges, organiser les relances et constituer un dossier documenté des démarches engagées et des réponses obtenues.",
  },
  {
    title: "Espaces réservés, en complément",
    traitement:
      "Analyser, lorsque cela est nécessaire, les espaces accessibles sur compte ou abonnement, qui restent eux aussi soumis aux règles applicables.",
  },
];

const STEPS = [
  {
    title: "Audit initial",
    desc: "Premier examen de la situation, des sources visibles et du périmètre envisagé.",
  },
  {
    title: "Cadrage",
    desc: "Définition d'un périmètre clair et des actions à mener dans la limite de l'accompagnement convenu.",
  },
  {
    title: "Appui",
    desc: "Préparation des demandes, suivi des échanges, relances et tenue du dossier.",
  },
  {
    title: "Restitution",
    desc: "Synthèse des démarches engagées, des réponses obtenues et des suites possibles.",
  },
];

const FORMULAS = [
  {
    label: "Niveau 01",
    title: "Audit initial",
    price: 100,
    finalite:
      "Évaluer une situation, repérer les sources principales et vérifier si une démarche est pertinente.",
    inclus: [
      "Premier examen des sources visibles",
      "Identification du périmètre",
      "Premières pistes documentées",
    ],
  },
  {
    label: "Niveau 02",
    title: "Intervention ciblée",
    price: 300,
    finalite:
      "Préparer une action limitée sur un périmètre clairement identifié.",
    inclus: [
      "Repérage des plateformes concernées",
      "Qualification des rediffusions",
      "Préparation des demandes",
      "Suivi initial des réponses",
    ],
  },
  {
    label: "Niveau 03",
    title: "Dossier complet",
    price: 600,
    finalite:
      "Organiser un dossier lorsque plusieurs sources ou démarches sont concernées.",
    inclus: [
      "Recensement des sources",
      "Organisation des éléments utiles",
      "Préparation des demandes",
      "Suivi des réponses",
    ],
  },
  {
    label: "Niveau 04",
    title: "Situation complexe",
    price: null,
    finalite:
      "Structurer une situation ancienne, dispersée ou sensible.",
    inclus: [
      "Cartographie étendue",
      "Sources multiples",
      "Coordination renforcée",
      "Orientation spécialisée si nécessaire",
    ],
  },
];

const EXCLUSIONS = [
  {
    title: "Contentieux",
    desc: "HorsIndex n'assure pas la représentation devant les juridictions.",
  },
  {
    title: "Consultation juridique individualisée",
    desc: "HorsIndex ne se substitue pas à un avocat ni à une profession réglementée.",
  },
  {
    title: "Garantie de résultat",
    desc: "Aucune suppression, désindexation, anonymisation ou correction ne peut être garantie à l'avance.",
  },
];

function SectionEyebrow({ n, label }) {
  return (
    <p className="label-eyebrow mb-6">
      <span className="text-[#B83A34]">{String(n).padStart(2, "0")}</span>
      {" / "}
      {label}
    </p>
  );
}

function ExpositionCard({ item, i, total }) {
  return (
    <li
      data-testid={`services-exposition-${i}`}
      className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col h-full"
    >
      <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
        {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </span>
      <h3 className="font-serif text-xl lg:text-[22px] text-[#181A1D] font-normal leading-snug tracking-tight mb-4 text-balance">
        {item.title}
      </h3>
      <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
        {item.traitement}
      </p>
      {item.link && (
        <Link
          to={item.link.to}
          data-testid="services-deref-link"
          className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] text-[#181A1D] font-normal underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#181A1D] transition-colors"
        >
          {item.link.label}
          <ArrowRight size={12} strokeWidth={1.5} />
        </Link>
      )}
    </li>
  );
}

function StepCard({ step, i }) {
  return (
    <li
      data-testid={`services-step-${i}`}
      className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col"
    >
      <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
        Étape {String(i + 1).padStart(2, "0")} / 04
      </span>
      <h3 className="font-serif text-2xl text-[#181A1D] font-normal leading-snug tracking-tight mb-3 text-balance">
        {step.title}
      </h3>
      <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
        {step.desc}
      </p>
    </li>
  );
}

function FormulaCard({ formula, i }) {
  return (
    <li
      data-testid={`services-formula-${i}`}
      className="bg-white rounded-md border border-[#E2DEDA] p-8 lg:p-9 flex flex-col h-full hover:border-[#B9B3AC] hover:shadow-[0_2px_10px_rgba(24,26,29,0.05)] transition-[border-color,box-shadow] duration-200"
    >
      <div className="flex items-start justify-between gap-3 mb-6 min-h-[2rem]">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] leading-[1.4]">
          {formula.label}
        </span>
        <span className="font-mono text-[10px] tracking-[0.22em] text-[#181A1D]/40 leading-[1.4] whitespace-nowrap flex-shrink-0">
          {String(i + 1).padStart(2, "0")} / 04
        </span>
      </div>

      <h3 className="font-serif text-[22px] lg:text-2xl text-[#181A1D] font-normal leading-snug tracking-tight mb-5 min-h-[2.75rem] text-balance">
        {formula.title}
      </h3>

      <div
        data-testid={`services-formula-${i}-finalite`}
        className="mb-6 pl-4 border-l-2 border-[#E2DEDA] min-h-[5.5rem]"
      >
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#666A70] mb-2">
          Finalité
        </p>
        <p className="text-[13.5px] lg:text-sm text-[#52565C] font-normal italic leading-relaxed text-pretty">
          {formula.finalite}
        </p>
      </div>

      <p data-testid={`services-formula-${i}-prix`} className="font-mono text-[14px] lg:text-[15px] font-medium tracking-tight text-[#181A1D] mb-6">
        {formula.price ? (
          <>
            <span className="block">À partir de {formatEUR(formula.price)}&nbsp;HT</span>
            <span className="block mt-1 text-[12px] font-normal text-[#8B8F95]">TVA en sus au taux applicable</span>
          </>
        ) : (
          "Sur devis"
        )}
      </p>

      <div className="h-[2px] w-8 bg-[#E94B3C] mb-5" />

      <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
        Pris en charge
      </p>

      <ul className="space-y-3 flex-1">
        {formula.inclus.map((b) => (
          <li
            key={b}
            className="text-[14px] lg:text-[15px] text-[#5A5E64] font-normal leading-relaxed flex items-start gap-3"
          >
            <span
              aria-hidden="true"
              className="mt-[7px] w-1 h-1 rounded-full bg-[#8B8F95] flex-shrink-0"
            />
            <span className="text-pretty">{b}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/contact"
        data-testid={`services-formula-${i}-cta`}
        className="mt-8 text-[11px] tracking-[0.18em] uppercase font-medium text-[#181A1D] inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
      >
        Présenter une situation
        <ArrowRight size={13} strokeWidth={1.5} />
      </Link>
    </li>
  );
}

export default function Services() {
  return (
    <PageLayout
      eyebrow="Services"
      title={<>Des services pour réduire ce qui est <em className="italic font-normal">visible</em> sur vous en ligne.</>}
      pattern="grid"
      intro="HorsIndex intervient prioritairement sur la surface publique d'exposition numérique : pages librement accessibles, résultats indexés, documents téléchargeables, cartographies et agrégations rattachées à une personne identifiable. L'accompagnement se décline en quatre niveaux, de l'audit initial à la situation complexe, selon une démarche graduée, documentée et ciblée."
      hideFooterCta
    >
      {/* 01 — Les situations prises en charge */}
      <section
        data-testid="services-section-01"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={1} label="Formes d'exposition" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Les situations prises en charge.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Les interventions sont organisées selon les formes
                d'exposition rencontrées, en concentrant l'action
                prioritaire sur ce qui est accessible sans compte, sans
                abonnement et sans motif particulier. La démarche est
                graduée et documentée : elle ne consiste pas à promettre
                d'« effacer Internet ».
              </p>
            </div>
          </div>
          <ul
            data-testid="services-expositions"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12"
          >
            {EXPOSITIONS.map((s, i) => (
              <ExpositionCard
                key={s.title}
                item={s}
                i={i}
                total={EXPOSITIONS.length}
              />
            ))}
          </ul>
        </div>
      </section>

      {/* 02 — Comment se déroule une intervention */}
      <section
        data-testid="services-section-02"
        className="bg-[#F2F0ED] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={2} label="Déroulement" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Du premier examen au bilan final.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Quatre temps lisibles, du premier contact à la synthèse
                finale. Le détail des opérations engagées est exposé sur
                la page Méthode.
              </p>
            </div>
          </div>
          <ol
            data-testid="services-steps"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12"
          >
            {STEPS.map((s, i) => (
              <StepCard key={s.title} step={s} i={i} />
            ))}
          </ol>
        </div>
      </section>

      {/* 03 — Formules d'intervention */}
      <section
        data-testid="services-section-03"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={3} label="Formules" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Formules et tarifs.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                Quatre niveaux pour adapter l'intensité de l'appui à la
                situation rencontrée. Les montants sont indicatifs,
                susceptibles d'évoluer selon le périmètre, et ne
                constituent pas des offres commerciales standardisées.
              </p>
            </div>
          </div>
          <ul
            data-testid="services-formulas"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4"
          >
            {FORMULAS.map((f, i) => (
              <FormulaCard key={f.title} formula={f} i={i} />
            ))}
          </ul>

          <p
            data-testid="services-pricing-notice"
            className="mt-10 text-[13px] text-[#666A70] font-normal leading-relaxed max-w-3xl"
          >
            Tarifs indicatifs. Toute prestation est précédée d'un devis établi
            dans le cadre défini par les{" "}
            <Link
              to="/conditions-generales"
              data-testid="services-link-cgv"
              className="text-[#33363B] underline decoration-[#D8D3CD] underline-offset-4 hover:text-[#B83A34] hover:decoration-[#E94B3C] transition-colors"
            >
              conditions générales
            </Link>. Les conditions fiscales et contractuelles applicables sont
            précisées avant toute prestation.
          </p>

          <p
            data-testid="services-formulas-disclaimer"
            className="mt-6 text-[13px] italic text-[#666A70] font-normal leading-relaxed max-w-3xl"
          >
            Tous les montants indiqués sont mentionnés à titre indicatif et
            ne constituent pas des offres commerciales standardisées. Ils
            sont susceptibles d'évoluer selon les particularités de chaque
            situation.
          </p>
        </div>
      </section>

      {/* 04 — Ce qui reste hors périmètre */}
      <section
        data-testid="services-section-04"
        className="bg-[#F2F0ED] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <SectionEyebrow n={4} label="Limites" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Ce qui reste hors périmètre.
              </h2>
            </div>
          </div>
          <ul
            data-testid="services-exclusions"
            className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10"
          >
            {EXCLUSIONS.map((e) => (
              <li
                key={e.title}
                className="border-l border-[#D8D3CD] pl-6 py-1 flex flex-col"
              >
                <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
                  Hors périmètre
                </span>
                <h3 className="font-serif text-xl text-[#181A1D] font-normal leading-snug tracking-tight mb-3 text-balance">
                  {e.title}
                </h3>
                <p className="text-sm text-[#666A70] font-normal leading-relaxed text-pretty">
                  {e.desc}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 05 — Pourquoi un audit initial ? */}
      <section
        data-testid="services-section-05"
        className="bg-[#181A1D]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#666A70] mb-6">
                05 / Étape suivante
              </p>
              <h2 className="font-serif font-normal text-white text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Pourquoi un audit initial&nbsp;?
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-8">
              <p className="text-[15px] lg:text-base text-[#B4B8BD] font-normal leading-relaxed max-w-2xl">
                L'audit initial sert de point d'entrée : il permet de
                vérifier la pertinence d'un accompagnement, de repérer
                les principales sources visibles et d'orienter vers le
                niveau d'appui le mieux adapté. Il évite d'engager des
                démarches mal calibrées.
              </p>
              <div>
                <Link
                  to="/audit-initial"
                  data-testid="services-cta-link"
                  className="inline-flex items-center gap-2 bg-white text-[#181A1D] px-7 py-3.5 text-[12px] tracking-[0.18em] uppercase font-medium rounded-md hover:bg-[#F2F0ED] transition-colors"
                >
                  Demander un audit initial
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
